import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatOnChainTokenAmount,
  type OnChainTokenBalance,
  type OnChainTokenSymbol,
  waitForOnChainTransaction,
} from '../lib/blockchain';
import {
  executeSwapWithWorldApp,
  executeSwapWithMetaMask,
  formatSwapAmount,
  getConfirmedSwapOutput,
  getSwapQuote,
  getSwapWorldScanUrl,
  isSupportedSwapPair,
  isSupportedSwapToken,
  WORLD_CHAIN_SWAP_CONTRACT_ADDRESS,
  type SwapQuote,
} from '../lib/swap';
import { buildWalletOperationIdempotencyKey, recordWalletOperationBestEffort } from '../lib/walletActivity';
import { walletAppDeepLink } from '../lib/config';
import WalletSourceIcon from './WalletSourceIcon';
import type { WalletBalanceSource } from '../lib/walletSource';
import { formatErrorWithCode, formatMessageWithCode } from '../i18n';
import { getSwapAvailabilityState } from '../lib/swapAvailability';

export type SwapTokenOption = {
  symbol: OnChainTokenSymbol;
  label: string;
  balance?: OnChainTokenBalance;
};

type SwapModalProps = {
  isOpen: boolean;
  initialInputSymbol?: OnChainTokenSymbol;
  initialOutputSymbol?: OnChainTokenSymbol;
  authUserId: string | null;
  walletAddress: string | null;
  isWorldApp: boolean;
  options: SwapTokenOption[];
  isBalanceLoading: boolean;
  balanceSource: WalletBalanceSource;
  metaMaskAddress: string | null;
  metaMaskOptions: SwapTokenOption[];
  isMetaMaskLoading: boolean;
  metaMaskError: string | null;
  onConnectMetaMask: () => Promise<void>;
  onClose: () => void;
  onSubmitted: () => void;
};

function isNonZeroBalance(option: SwapTokenOption): boolean {
  return Boolean(option.balance && !option.balance.error && option.balance.raw > 0n);
}

function getDefaultInput(
  options: SwapTokenOption[],
  preferredSymbol?: OnChainTokenSymbol,
): SwapTokenOption | undefined {
  return options.find((option) => option.symbol === preferredSymbol)
    ?? options.find((option) => option.symbol === 'GGMOMOMO' && isNonZeroBalance(option))
    ?? options.find((option) => option.symbol === 'BBT' && isNonZeroBalance(option))
    ?? options.find((option) => option.symbol === 'WLD' && isNonZeroBalance(option))
    ?? options[0];
}

type SwapFeedback = {
  kind: 'success' | 'error' | 'pending';
  title: string;
  message: string;
  inputAmount: string;
  inputSymbol?: OnChainTokenSymbol;
  outputAmount?: string;
  outputSymbol?: OnChainTokenSymbol;
  outputLabel?: string;
  txHash?: string;
};

export default function SwapModal({
  isOpen,
  initialInputSymbol,
  initialOutputSymbol,
  authUserId,
  walletAddress,
  isWorldApp,
  options,
  isBalanceLoading,
  balanceSource,
  metaMaskAddress,
  metaMaskOptions,
  isMetaMaskLoading,
  metaMaskError,
  onConnectMetaMask,
  onClose,
  onSubmitted,
}: SwapModalProps) {
  const { t } = useTranslation();
  const authUserIdRef = useRef(authUserId);
  const walletAddressRef = useRef(walletAddress);
  const metaMaskAddressRef = useRef(metaMaskAddress);
  authUserIdRef.current = authUserId;
  walletAddressRef.current = walletAddress;
  metaMaskAddressRef.current = metaMaskAddress;
  const [inputSymbol, setInputSymbol] = useState<OnChainTokenSymbol | null>(null);
  const [outputSymbol, setOutputSymbol] = useState<OnChainTokenSymbol | null>(null);
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState<SwapQuote | null>(null);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [txHashes, setTxHashes] = useState<string[]>([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [swapFeedback, setSwapFeedback] = useState<SwapFeedback | null>(null);
  const initializedForKeyRef = useRef<string | null>(null);
  const isWorldAppSource = isWorldApp && balanceSource === 'world-app';
  const sourceOptions = balanceSource === 'world-app' ? options : metaMaskOptions;
  const sourceAddressForInitialization = balanceSource === 'world-app' ? walletAddress : metaMaskAddress;
  const initializationKey = [
    balanceSource,
    sourceAddressForInitialization?.toLowerCase() ?? '',
    initialInputSymbol ?? '',
    initialOutputSymbol ?? '',
  ].join(':');
  const availableOptions = useMemo(
    () => sourceOptions.filter((option) => (
      option.balance
      && !option.balance.error
      && isSupportedSwapToken(option.symbol)
    )),
    [sourceOptions],
  );

  const inputOption = availableOptions.find((option) => option.symbol === inputSymbol)
    ?? getDefaultInput(availableOptions, initialInputSymbol);
  const outputOptions = inputOption
    ? availableOptions.filter((option) => isSupportedSwapPair(inputOption.symbol, option.symbol))
    : [];
  const outputOption = outputOptions.find((option) => option.symbol === outputSymbol)
    ?? (inputOption?.symbol === initialInputSymbol
      ? outputOptions.find((option) => option.symbol === initialOutputSymbol)
      : undefined)
    ?? outputOptions[0];
  const inputBalance = inputOption?.balance;
  const outputBalance = outputOption?.balance;
  const inputDecimals = inputBalance?.decimals ?? 18;
  const outputDecimals = outputBalance?.decimals ?? 18;

  useEffect(() => {
    if (!isOpen) {
      initializedForKeyRef.current = null;
      return;
    }
    if (isBalanceLoading || initializedForKeyRef.current === initializationKey) return;
    const defaultInput = getDefaultInput(availableOptions, initialInputSymbol);
    const defaultOutput = defaultInput
      ? availableOptions.find((option) => (
        option.symbol === initialOutputSymbol
        && isSupportedSwapPair(defaultInput.symbol, option.symbol)
      ))
        ?? availableOptions.find((option) => isSupportedSwapPair(defaultInput.symbol, option.symbol))
      : undefined;
    setInputSymbol(defaultInput?.symbol ?? null);
    setOutputSymbol(defaultOutput?.symbol ?? null);
    setAmount('');
    setQuote(null);
    setIsQuoting(false);
    setIsSubmitting(false);
    setIsConfirming(false);
    setTxHashes([]);
    setStatusMessage('');
    setErrorMessage('');
    initializedForKeyRef.current = initializationKey;
  }, [availableOptions, initialInputSymbol, initialOutputSymbol, initializationKey, isBalanceLoading, isOpen]);

  useEffect(() => {
    if (isOpen) setSwapFeedback(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !inputSymbol || !outputSymbol || !inputBalance || !outputBalance || !amount.trim()) {
      setQuote(null);
      setIsQuoting(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setIsQuoting(true);
      setQuote(null);
      setErrorMessage('');
      void getSwapQuote({
        tokenIn: inputSymbol,
        tokenOut: outputSymbol,
        amount,
        decimals: inputDecimals,
        rawBalance: inputBalance.raw,
      })
        .then((nextQuote) => {
          if (!cancelled) setQuote(nextQuote);
        })
        .catch((error: unknown) => {
          if (!cancelled) {
            setErrorMessage(t('swap.quoteError', { error: formatErrorWithCode(error, t, 'errors.swapFailed', 'QUOTE_FAILED') }));
          }
        })
        .finally(() => {
          if (!cancelled) setIsQuoting(false);
        });
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [amount, inputBalance, inputDecimals, inputSymbol, isOpen, outputBalance, outputSymbol, t]);

  if (!isOpen) return null;

  const handleSetMaxAmount = () => {
    if (inputBalance) setAmount(inputBalance.balance);
  };

  const handleChangeInput = (nextSymbol: OnChainTokenSymbol) => {
    setInputSymbol(nextSymbol);
    if (!outputSymbol || !isSupportedSwapPair(nextSymbol, outputSymbol)) {
      setOutputSymbol(availableOptions.find((option) => isSupportedSwapPair(nextSymbol, option.symbol))?.symbol ?? null);
    }
    setAmount('');
    setQuote(null);
  };

  const handleSwapDirection = () => {
    if (!outputSymbol || !inputSymbol) return;
    setInputSymbol(outputSymbol);
    setOutputSymbol(inputSymbol);
    setAmount('');
    setQuote(null);
    setErrorMessage('');
  };

  const handleSubmit = async () => {
    setErrorMessage('');
    setStatusMessage('');

    if (!isWorldAppSource && balanceSource === 'world-app') {
      setErrorMessage(formatMessageWithCode(t('swap.browserMetaMaskOnly'), t, 'METAMASK_REQUIRED'));
      return;
    }

    const activeWalletAddress = balanceSource === 'metamask' ? metaMaskAddress : walletAddress;
    if (!activeWalletAddress) {
      setErrorMessage(formatMessageWithCode(t(isWorldAppSource ? 'swap.loginForAddress' : 'swap.connectForAddress'), t, 'WALLET_AUTH_REQUIRED'));
      return;
    }
    if (!inputSymbol || !outputSymbol || !inputBalance || !outputBalance) {
      setErrorMessage(formatMessageWithCode(t('swap.noTokenData'), t, 'TOKEN_DATA_UNAVAILABLE'));
      return;
    }
    if (!isSupportedSwapPair(inputSymbol, outputSymbol)) {
      setErrorMessage(formatMessageWithCode(t('swap.unsupportedPair'), t, 'UNSUPPORTED_SWAP_PAIR'));
      return;
    }
    if (!amount.trim()) {
      setErrorMessage(formatMessageWithCode(t('swap.enterAmount'), t, 'SWAP_AMOUNT_REQUIRED'));
      return;
    }
    if (!quote) {
      setErrorMessage(formatMessageWithCode(t('swap.waitForQuote'), t, 'SWAP_QUOTE_REQUIRED'));
      return;
    }

    setIsSubmitting(true);
    const requestAuthUserId = authUserId;
    const requestWalletAddress = activeWalletAddress;
    const requestBalanceSource = balanceSource;
    try {
      const executeSwap = isWorldAppSource ? executeSwapWithWorldApp : executeSwapWithMetaMask;
      const result = await executeSwap({
        tokenIn: inputSymbol,
        tokenOut: outputSymbol,
        amount,
        walletAddress: activeWalletAddress,
        rawBalance: inputBalance.raw,
        decimals: inputBalance.decimals,
        transactionNotice: isWorldAppSource ? {
          title: t('swap.transactionTitle'),
          description: t('swap.transactionDescription', {
            amount: formatSwapAmount(quote.amountIn, inputBalance.decimals),
            inputSymbol,
            outputSymbol,
          }),
        } : undefined,
        onProgress: (progress) => {
          const messages = {
            checking: t('swap.checkingApproval'),
            approval: isWorldAppSource ? t('swap.approveWorldApp') : t('swap.approveMetaMask'),
            'approval-and-swap': t('swap.approveAndSwap'),
            quoting: t('swap.updatingQuote'),
            swap: isWorldAppSource ? t('swap.confirmWorldApp') : t('swap.confirmMetaMask'),
            confirming: t('swap.waitingChain'),
          } as const;
          setStatusMessage(messages[progress]);
        },
      });

      const hashes = [...result.approvalTxHashes, result.txHash];
      setTxHashes(hashes);
      setStatusMessage(t('swap.submitted'));
      setIsSubmitting(false);
      setIsConfirming(true);

      void waitForOnChainTransaction(result.txHash)
        .then(async (confirmation) => {
          setIsConfirming(false);
          if (
            confirmation === 'confirmed'
            && requestAuthUserId
            && authUserIdRef.current === requestAuthUserId
            && (
              requestBalanceSource === 'metamask'
                ? metaMaskAddressRef.current === requestWalletAddress
                : walletAddressRef.current === requestWalletAddress
            )
          ) {
            let confirmedOutput: bigint | null = null;
            try {
              confirmedOutput = await getConfirmedSwapOutput(result.txHash);
            } catch {
              // The receipt is already confirmed; fall back to the quote if the log read is unavailable.
            }
            const outputAmount = confirmedOutput ?? result.quote.quotedAmountOut;
            await recordWalletOperationBestEffort({
              operation_type: 'swap',
              idempotency_key: buildWalletOperationIdempotencyKey('swap', result.txHash),
              wallet_address: activeWalletAddress,
              reference_type: 'wallet_swap',
              metadata: {
                token_in: inputSymbol,
                token_out: outputSymbol,
                quoted_amount_out_base_units: result.quote.quotedAmountOut.toString(),
              },
              legs: [
                ...result.approvalTxHashes.map((approvalTxHash, index) => ({
                  leg_type: 'approval' as const,
                  sequence_no: index,
                  chain_id: 480,
                  tx_hash: approvalTxHash,
                  token_address: inputBalance.address,
                  token_symbol: inputSymbol,
                })),
                {
                  leg_type: 'swap',
                  sequence_no: result.approvalTxHashes.length,
                  chain_id: 480,
                  tx_hash: result.txHash,
                  user_op_hash: result.userOpHash,
                  from_address: activeWalletAddress,
                  to_address: WORLD_CHAIN_SWAP_CONTRACT_ADDRESS,
                  token_address: inputBalance.address,
                  token_symbol: inputSymbol,
                  amount_base_units: result.quote.amountIn.toString(),
                  amount_out_base_units: outputAmount.toString(),
                },
              ],
            });
            setStatusMessage(t('swap.completed', {
              amount: formatSwapAmount(outputAmount, outputBalance.decimals),
              symbol: outputSymbol,
            }));
            setSwapFeedback({
              kind: 'success',
              title: t('swap.successTitle'),
              message: t('swap.successMessage', { source: isWorldAppSource ? 'World App' : 'MetaMask' }),
              inputAmount: formatSwapAmount(result.quote.amountIn, inputBalance.decimals),
              inputSymbol,
              outputAmount: formatSwapAmount(outputAmount, outputBalance.decimals),
              outputSymbol,
              outputLabel: confirmedOutput ? t('swap.confirmedOutput') : t('swap.quotedOutput'),
              txHash: result.txHash,
            });
          } else {
            setStatusMessage(t('swap.executionFailedStatus'));
            setSwapFeedback({
              kind: 'error',
              title: t('swap.failedTitle'),
              message: t('swap.failedMessage'),
              inputAmount: formatSwapAmount(result.quote.amountIn, inputBalance.decimals),
              inputSymbol,
              outputSymbol,
              txHash: result.txHash,
            });
          }
          onSubmitted();
        })
        .catch((error: unknown) => {
          setIsConfirming(false);
          setStatusMessage(t('swap.submittedSlow'));
          const localizedMessage = formatErrorWithCode(error, t, 'errors.swapFailed');
          setErrorMessage(localizedMessage);
          setSwapFeedback({
            kind: 'pending',
            title: t('swap.pendingTitle'),
            message: localizedMessage,
            inputAmount: amount.trim(),
            inputSymbol,
            outputSymbol,
            txHash: result.txHash,
          });
          onSubmitted();
        });
    } catch (error) {
      setIsSubmitting(false);
      const message = formatErrorWithCode(error, t, 'errors.swapFailed');
      setErrorMessage(message);
      setSwapFeedback({
        kind: 'error',
        title: t('swap.failedTitle'),
        message,
        inputAmount: amount.trim(),
        inputSymbol: inputSymbol ?? undefined,
        outputSymbol: outputSymbol ?? undefined,
      });
    }
  };

  const canSignSwap = isWorldAppSource || balanceSource === 'metamask';
  const isBrowserWorldAppView = !isWorldApp && balanceSource === 'world-app';
  const canSubmit = Boolean(
    inputBalance
      && outputBalance
      && inputSymbol
      && outputSymbol
      && amount.trim()
      && quote
      && !isQuoting
      && !isSubmitting
      && !isConfirming
      && canSignSwap,
  );
  const sourceAddress = balanceSource === 'metamask' ? metaMaskAddress : walletAddress;
  const availabilityState = getSwapAvailabilityState({
    isBalanceLoading,
    availableOptionsCount: availableOptions.length,
    outputOptionsCount: outputOptions.length,
  });

  return (
    <>
      <div
        className="modal-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !isSubmitting && !isConfirming) onClose();
        }}
      >
      <section className="swap-token-modal" role="dialog" aria-modal="true" aria-labelledby="swap-token-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">WORLD CHAIN SWAP</p>
            <h2 id="swap-token-title">{t('swap.title')}</h2>
          </div>
          <button className="modal-close-button" type="button" onClick={onClose} disabled={isSubmitting} aria-label={t('swap.close')}>
            ×
          </button>
        </div>

        <div className="swap-source-badge">
          <WalletSourceIcon source={balanceSource} />
          <div>
            <span>{t('swap.currentAccount', { source: balanceSource === 'world-app' ? 'World App' : 'MetaMask' })}</span>
            <code>{sourceAddress ? `${sourceAddress.slice(0, 6)}…${sourceAddress.slice(-4)}` : t('common.notConnected')}</code>
          </div>
        </div>

        {isBrowserWorldAppView && (
          <div className="send-environment-panel">
            <p>{t('swap.completeInWorldApp')}</p>
            {walletAppDeepLink && (
              <a className="send-environment-link" href={walletAppDeepLink}>
                {t('swap.openWorldApp')}
              </a>
            )}
          </div>
        )}

        {!isBrowserWorldAppView && (
          balanceSource === 'metamask' && !metaMaskAddress ? (
            <div className="swap-token-connect">
              <p className="send-token-empty">{t('swap.browserMetaMaskDescription')}</p>
              {metaMaskError && <p className="send-token-error" role="alert">{metaMaskError}</p>}
              <button className="auth-button" type="button" onClick={() => void onConnectMetaMask()} disabled={isMetaMaskLoading}>
                {isMetaMaskLoading ? t('common.connect') : t('common.connectMetaMask')}
              </button>
            </div>
          ) : availabilityState === 'loading' ? (
            <p className="send-token-empty">{t('common.loading')}</p>
          ) : availabilityState === 'empty' ? (
            <p className="send-token-empty">{t('swap.noPairs')}</p>
          ) : (
            <div className="send-token-form">
            <label className="send-token-field">
              <span>{t('swap.payToken')}</span>
              <select
                value={inputOption?.symbol ?? ''}
                onChange={(event) => handleChangeInput(event.target.value as OnChainTokenSymbol)}
                disabled={isSubmitting || isConfirming}
              >
                {availableOptions.map((option) => (
                  <option key={option.symbol} value={option.symbol}>{option.symbol} · {option.label}</option>
                ))}
              </select>
            </label>

            <div className="swap-token-direction">
              <span>{t('swap.direction')}</span>
              <button type="button" onClick={handleSwapDirection} disabled={isSubmitting || isConfirming} aria-label={t('swap.reverseDirection')}>
                <svg
                  className="swap-direction-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M8 5v14m0 0-3-3m3 3 3-3M16 19V5m0 0 3 3m-3-3-3 3" />
                </svg>
              </button>
            </div>

            <label className="send-token-field">
              <span>{t('swap.receiveToken')}</span>
              <select
                value={outputOption?.symbol ?? ''}
                onChange={(event) => {
                  setOutputSymbol(event.target.value as OnChainTokenSymbol);
                  setAmount('');
                  setQuote(null);
                }}
                disabled={isSubmitting || isConfirming}
              >
                {outputOptions
                  .map((option) => (
                    <option key={option.symbol} value={option.symbol}>{option.symbol} · {option.label}</option>
                  ))}
              </select>
            </label>

            {inputBalance && (
              <div className="send-token-available">
                <span>{t('send.availableBalance')}</span>
                <div className="send-token-available-value">
                  <strong>{formatOnChainTokenAmount(inputBalance.raw, inputBalance.decimals)} {inputSymbol}</strong>
                  <button type="button" onClick={handleSetMaxAmount} disabled={isSubmitting || isConfirming}>{t('send.max')}</button>
                </div>
              </div>
            )}

            <label className="send-token-field">
              <span>{t('swap.payAmount')}</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                placeholder="0.00"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                disabled={isSubmitting || isConfirming}
              />
            </label>

            {isQuoting && <p className="send-token-status" role="status">{t('swap.quoteLoading')}</p>}
            {quote && outputBalance && outputSymbol && (
              <div className="swap-token-quote" aria-live="polite">
                <div><span>{t('swap.expectedReceive')}</span><strong>≈ {formatSwapAmount(quote.quotedAmountOut, outputDecimals)} {outputSymbol}</strong></div>
                <div><span>{t('swap.minimumReceive')}</span><strong>{formatSwapAmount(quote.minimumAmountOut, outputDecimals)} {outputSymbol}</strong></div>
              </div>
            )}
            {errorMessage && <p className="send-token-error" role="alert">{errorMessage}</p>}
            {statusMessage && <p className="send-token-status" role="status">{statusMessage}</p>}
            {txHashes.map((hash) => (
              <a
                className="send-token-hash"
                href={getSwapWorldScanUrl(hash)}
                target={isWorldApp ? undefined : '_blank'}
                rel={isWorldApp ? undefined : 'noreferrer'}
                key={hash}
              >
                {t('swap.viewTransaction', { hash: `${hash.slice(0, 10)}…${hash.slice(-8)}` })}
              </a>
            ))}

            <div className="send-token-actions">
              <button className="text-button" type="button" onClick={onClose} disabled={isSubmitting}>
                {isConfirming ? t('common.backgroundConfirming') : t('common.cancel')}
              </button>
              <button className="auth-button" type="button" onClick={() => void handleSubmit()} disabled={!canSubmit}>
                {isSubmitting
                  ? t('swap.waitingWallet', { source: isWorldAppSource ? 'World App' : 'MetaMask' })
                  : isConfirming ? t('common.confirming') : t('swap.confirm')}
              </button>
            </div>
            </div>
          )
        )}
      </section>
      </div>
      {swapFeedback && (
        <div className="swap-feedback-overlay" role="presentation">
          <section
            className={`swap-feedback-dialog is-${swapFeedback.kind}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="swap-feedback-title"
          >
            <div className="swap-feedback-icon" aria-hidden="true">
              {swapFeedback.kind === 'success' ? '✓' : swapFeedback.kind === 'pending' ? '…' : '×'}
            </div>
            <p className="eyebrow">{swapFeedback.kind === 'success' ? 'WORLD CHAIN CONFIRMED' : 'WORLD CHAIN SWAP'}</p>
            <h2 id="swap-feedback-title">{swapFeedback.title}</h2>
            <p className="swap-feedback-message">{swapFeedback.message}</p>

            <div className="swap-feedback-details">
              <div>
                <span>{t('swap.payAmount')}</span>
                <strong>{swapFeedback.inputAmount} {swapFeedback.inputSymbol ?? ''}</strong>
              </div>
              {swapFeedback.outputAmount && swapFeedback.outputSymbol && (
                <div>
                  <span>{swapFeedback.outputLabel ?? t('swap.receiveToken')}</span>
                  <strong>{swapFeedback.outputAmount} {swapFeedback.outputSymbol}</strong>
                </div>
              )}
            </div>

            {swapFeedback.txHash && (
              <a
                className="send-token-hash"
                href={getSwapWorldScanUrl(swapFeedback.txHash)}
                target={isWorldApp ? undefined : '_blank'}
                rel={isWorldApp ? undefined : 'noreferrer'}
              >
                {t('swap.viewTransaction', { hash: `${swapFeedback.txHash.slice(0, 10)}…${swapFeedback.txHash.slice(-8)}` })}
              </a>
            )}

            <button className="auth-button" type="button" onClick={() => setSwapFeedback(null)}>
              {t('swap.closeFeedback')}
            </button>
          </section>
        </div>
      )}
    </>
  );
}
